// SPDX-License-Identifier: GPL-2.0-or-later
// Nomes de jogador (RN-NOM-01..03, spec plan/specs/names.md): validação como check_name
// (src/commont.cpp:190), comparação como makeComparable (src/auth.cpp:88), convidados e clan tag.
/** maxPlayerNameLength (src/commont.h:53). */
export const MAX_NAME_LENGTH = 15;
/** Tag do clã cadastrado (OG-270, D-47). */
export const MAX_CLAN_TAG_LENGTH = 3;
export const MIN_CLAN_NAME_LENGTH = 3;
export const MAX_CLAN_NAME_LENGTH = 24;
/** Marca dos convidados em jogo (RN-NOM-02). */
export const GUEST_SUFFIX = '*';
/** is_nonprintable_char (src/utility.cpp:296); acima de 255 também: as fontes só têm Latin-1. */
function nonPrintable(code) {
    return code < 32 || (code >= 128 && code <= 159) || code > 255;
}
const isSpace = (ch) => ch === ' ' || ch === '\u00a0';
/** check_name: até 15 caracteres imprimíveis, não só espaços, sem espaço (ou NBSP) nas pontas. */
export function checkName(name, max = MAX_NAME_LENGTH) {
    if (name.length > max)
        return false;
    if ([...name].every((ch) => isSpace(ch)))
        return false;
    for (let i = 0; i < name.length; i++)
        if (nonPrintable(name.charCodeAt(i)))
            return false;
    return !isSpace(name[0]) && !isSpace(name[name.length - 1]);
}
/** makeComparable do original (locale "C": só [A-Za-z0-9] são alfanuméricos). */
export function makeComparable(name) {
    let out = '';
    for (const ch of name) {
        if (/[A-Za-z0-9]/.test(ch))
            out += ch.toLowerCase();
        else if (out !== '')
            out += '.';
    }
    return out.replace(/\.+$/, '');
}
const FOLD = { ß: 'ss', æ: 'ae', Æ: 'ae', ø: 'o', Ø: 'o', ð: 'd', Ð: 'd', þ: 'th', Þ: 'th', µ: 'u' };
/** Tira acentos de Latin-1 (é→e, ß→ss...) antes de comparar (divergência 2 da spec). */
export function foldAccents(name) {
    return [...name]
        .map((ch) => FOLD[ch] ?? ch.normalize('NFD').replace(/[̀-ͯ]/g, ''))
        .join('');
}
/** Forma usada para saber se dois nomes são "o mesmo" (unicidade e proteção de nome). */
export function comparableName(name) {
    return makeComparable(foldAccents(name));
}
/** Nome registrável: válido e com alguma letra ou dígito (como as linhas do auth.txt). */
export function checkRegisteredName(name) {
    return checkName(name) && comparableName(name) !== '';
}
/** Nome exibido de um convidado: o escolhido (cortado para caber) + `*`. */
export function guestName(base) {
    return base.slice(0, MAX_NAME_LENGTH - GUEST_SUFFIX.length).replace(/[ \u00a0]+$/, '') + GUEST_SUFFIX;
}
/** Clan tag: 1 a 3 caracteres com as regras do nome, sem parênteses (exibida como "(TAG) nome"). */
export function checkClanTag(tag) {
    return tag.length >= 1 && tag.length <= MAX_CLAN_TAG_LENGTH && checkName(tag) && !/[()]/.test(tag);
}
/** Nome de clã (OG-270): de 3 a 24 caracteres com as regras do nome. */
export function checkClanName(name) {
    return name.length >= MIN_CLAN_NAME_LENGTH && checkName(name, MAX_CLAN_NAME_LENGTH);
}
/**
 * Como a tag do clã aparece junto do nome (OG-338): (Dk) Felix, [Dk] Felix, Dk-Felix, Dk.Felix, Dk|Felix.
 * A mesma conta está na função SQL `clan_label` (migration 0028).
 */
export const CLAN_TAG_STYLES = ['paren', 'bracket', 'dash', 'dot', 'pipe'];
/** Tag decorada pelo estilo: "(Dk)", "[Dk]", "Dk-", "Dk.", "Dk|". */
export function clanLabel(tag, style) {
    switch (style) {
        case 'bracket':
            return `[${tag}]`;
        case 'dash':
            return `${tag}-`;
        case 'dot':
            return `${tag}.`;
        case 'pipe':
            return `${tag}|`;
        default:
            return `(${tag})`;
    }
}
/**
 * Nome como aparece no chat e no placar (src/server.cpp:1513): `label` é a tag já decorada (OG-338).
 * Entre parênteses ou colchetes vem com espaço, "(Dk) Felix"; os outros estilos colam no nome, "Dk.Felix".
 */
export function chatName(name, label) {
    if (!label)
        return name;
    if (/[)\]]$/.test(label))
        return `${label} ${name}`;
    if (/[-.|]$/.test(label))
        return `${label}${name}`;
    return `(${label}) ${name}`; // tag pura (replays e salas de antes do OG-338)
}
//# sourceMappingURL=names.js.map